/**
 * 問診票編集ビュー（フォームUI）
 * レビュー完了・レビュー中トグル、フォームによる編集、保存
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Loader2, Save, ArrowLeft } from 'lucide-react';
import {
  Complaint1Section,
  Complaint2Section,
  Complaint3Section,
  Complaint4Section,
  Complaint5Section,
  Complaint6Section,
} from '../../pages/admin/QuestionnaireFormSections';
import {
  questionnaireJsonToFormData,
  formDataToQuestionnaireJson,
  type QuestionnaireFormData,
} from '../../lib/questionnaireFormAdapter';
import { ScrollArea } from '../ui/scroll-area';

interface QuestionnaireEditViewProps {
  questionnaire: {
    id: number;
    questionnaire_data: {
      questionnaire_json?: Record<string, unknown>;
      questionnaire_text?: string;
      [key: string]: unknown;
    };
    review_status: string;
  };
  onBack: () => void;
  onUpdate: (id: number, data: object) => Promise<void>;
  onUpdateReviewStatus: (id: number, status: 'approved' | 'draft' | 'in_review') => Promise<void>;
}

export function QuestionnaireEditView({
  questionnaire,
  onBack,
  onUpdate,
  onUpdateReviewStatus,
}: QuestionnaireEditViewProps) {
  const [formData, setFormData] = useState<QuestionnaireFormData>({});
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const busy = useRef(false);

  const isApproved = questionnaire.review_status === 'approved';

  const qJson = (questionnaire.questionnaire_data?.questionnaire_json ?? questionnaire.questionnaire_data) as Record<string, unknown>;

  useEffect(() => {
    setFormData(questionnaireJsonToFormData(qJson));
  }, [questionnaire.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((key: string, value: string | string[] | boolean) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      setSaving(true);
      const json = formDataToQuestionnaireJson(formData);
      await onUpdate(questionnaire.id, { ...questionnaire.questionnaire_data, questionnaire_json: json, questionnaire_text: '' });
    } catch (e) {
      console.error('問診票保存エラー:', e);
      alert('保存に失敗しました。');
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  const handleToggleReview = async (checked: boolean) => {
    if (busy.current) return;
    busy.current = true;
    try {
      setToggling(true);
      if (checked) {
        await onUpdate(questionnaire.id, {
          ...questionnaire.questionnaire_data,
          questionnaire_json: formDataToQuestionnaireJson(formData),
          questionnaire_text: '',
        });
      }
      await onUpdateReviewStatus(questionnaire.id, checked ? 'approved' : 'in_review');
    } catch (error) {
      console.error('問診票レビュー更新エラー:', error);
      alert('保存またはレビュー状態の更新に失敗しました。入力内容を確認して再試行してください。');
    } finally {
      busy.current = false;
      setToggling(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack} disabled={saving || toggling}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              一覧へ
            </Button>
            <CardTitle className="text-lg">問診票 #{questionnaire.id} 編集</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">レビュー中</span>
            <Switch
              checked={isApproved}
              onCheckedChange={handleToggleReview}
              disabled={saving || toggling}
            />
            <span className="text-sm text-muted-foreground">レビュー完了</span>
            {toggling && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ScrollArea className="h-[calc(100vh-220px)] pr-4">
          <fieldset className="space-y-8" disabled={isApproved || saving || toggling}>
            <Complaint1Section formData={formData} onChange={handleChange} />
            <Complaint2Section formData={formData} onChange={handleChange} />
            <Complaint3Section formData={formData} onChange={handleChange} />
            <Complaint4Section formData={formData} onChange={handleChange} />
            <Complaint5Section formData={formData} onChange={handleChange} />
            <Complaint6Section formData={formData} onChange={handleChange} />
          </fieldset>
        </ScrollArea>

        <div className="flex gap-2 pt-4 border-t">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isApproved || saving || toggling}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            保存
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
